import { useEffect, useState } from "react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Card } from "../components/ui/card.jsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SpeciesLibrary(){
  const [category, setCategory] = useState("all");
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const categories = [
    { value: "all", label: "All Species" },
    { value: "Large Mammals", label: "Large Mammals" },
    { value: "Small Mammals", label: "Small Mammals" },
    { value: "Upland Birds", label: "Upland Birds" },
    { value: "Waterfowl (Puddle Ducks)", label: "Waterfowl (Puddle Ducks)" },
    { value: "Waterfowl (Diving Ducks)", label: "Waterfowl (Diving Ducks)" },
    { value: "Waterfowl (Geese)", label: "Waterfowl (Geese)" },
    { value: "Wetland Birds", label: "Wetland Birds" },
    { value: "Birds of Prey", label: "Birds of Prey" },
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const params = category === "all" ? {} : { category };
        const res = await axios.get(`${API}/species`, { params });
        if (!mounted) return;
        const loadedItems = res.data?.items || [];
        setAllItems(loadedItems);
        setItems(loadedItems);
      } catch (e) {
        console.error("Failed to load species:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [category]);

  // Filter items based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setItems(allItems);
    } else {
      const filtered = allItems.filter(item => 
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.physical_description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setItems(filtered);
    }
  }, [searchQuery, allItems]);

  return (
    <div className="container mx-auto px-6 py-10" data-testid="species-library-page">
      <div className="mb-8">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold font-serif text-white" data-testid="species-library-title">
              Species Anatomy & Habitat
            </h1>
            <p className="text-white/70 text-sm mt-2 max-w-3xl" data-testid="species-library-subtitle">
              Comprehensive guide to North American game species. Learn physical identification features, behavior patterns, and habitat preferences for 100 species across mammals, birds, and waterfowl.
            </p>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
          {/* Search Bar */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <input
                type="text"
                placeholder="Search species by name, category, or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-12 px-4 pr-10 bg-white/5 border border-white/15 text-white placeholder-white/50 rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                data-testid="species-search-input"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <svg className="w-5 h-5 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Category Filter */}
          <div className="min-w-[280px]" data-testid="species-category-select">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-white/5 border-white/15 text-white h-12 rounded-full" data-testid="species-category-trigger">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value} data-testid={`species-category-item-${cat.value}`}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Search Results Count */}
        {searchQuery && (
          <div className="mt-3 text-white/70 text-sm">
            {items.length > 0 ? (
              <span>Found {items.length} species matching "{searchQuery}"</span>
            ) : (
              <span>No species found matching "{searchQuery}"</span>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="species-library-loading">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="overflow-hidden hh-card">
              <div className="h-48 bg-white/10 animate-pulse" />
              <div className="p-5 space-y-3">
                <div className="h-4 bg-white/10 rounded animate-pulse" />
                <div className="h-3 bg-white/10 rounded animate-pulse w-3/4" />
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="species-library-grid">
            {items.map((species) => (
              <Card key={species.id} className="overflow-hidden hh-card text-white group hover:scale-[1.02] transition-transform" data-testid={`species-card-${species.id}`}>
                <div className="aspect-[4/3] overflow-hidden relative bg-gradient-to-br from-emerald-900/40 to-slate-900/40">
                  <img 
                    src={species.image_url.startsWith('/api/') || species.image_url.startsWith('/static/') ? `${BACKEND_URL}${species.image_url}` : species.image_url} 
                    alt={species.name} 
                    className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition-opacity"
                  />
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium">
                    {species.category}
                  </div>
                  {species.image_attribution_required && species.image_artist && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/75 backdrop-blur-sm px-3 py-1.5 text-[10px] text-white/90">
                      Photo: {species.image_artist} {species.image_license && `• ${species.image_license}`}
                      {species.image_license_url && (
                        <a 
                          href={species.image_license_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="ml-1.5 underline hover:text-white transition-colors"
                        >
                          (License)
                        </a>
                      )}
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-xl font-bold mb-3" data-testid={`species-name-${species.id}`}>
                    {species.name}
                  </h3>
                  
                  <div className="space-y-3">
                    {species.conservation_status && (
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
                        <h4 className="text-xs font-semibold text-blue-300 mb-1">Conservation Status</h4>
                        <p className="text-sm text-white/90 font-medium">
                          {species.conservation_status}
                        </p>
                      </div>
                    )}

                    {species.best_states_to_hunt && species.best_states_to_hunt.length > 0 && (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        <h4 className="text-xs font-semibold text-amber-300 mb-1">Best States to Hunt</h4>
                        <p className="text-sm text-white/90 font-medium">
                          {species.best_states_to_hunt.join(', ')}
                        </p>
                      </div>
                    )}

                    <div>
                      <h4 className="text-sm font-semibold text-emerald-400 mb-1">Physical Description</h4>
                      <p className="text-sm text-white/85 leading-relaxed">
                        {species.physical_description}
                      </p>
                    </div>

                    {species.anatomy_notes && (
                      <div>
                        <h4 className="text-sm font-semibold text-cyan-400 mb-1">Anatomy & Identification</h4>
                        <p className="text-sm text-white/85 leading-relaxed">
                          {species.anatomy_notes}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-400 mb-1">Behavior & Range</h4>
                      <p className="text-sm text-white/85 leading-relaxed">
                        {species.behavior_and_range}
                      </p>
                    </div>

                    {species.habitat_details && (
                      <div>
                        <h4 className="text-sm font-semibold text-green-400 mb-1">Habitat Details</h4>
                        <p className="text-sm text-white/85 leading-relaxed">
                          {species.habitat_details}
                        </p>
                      </div>
                    )}

                    {species.best_time_to_harvest && (
                      <div>
                        <h4 className="text-sm font-semibold text-orange-400 mb-1">Best Time to Harvest</h4>
                        <p className="text-sm text-white/85 leading-relaxed font-medium">
                          {species.best_time_to_harvest}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {items.length === 0 && !loading && (
            <Card className="p-8 text-center hh-card text-white">
              <p className="text-white/70">No species found in this category.</p>
            </Card>
          )}

          {items.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <p className="text-xs text-white/60 text-center max-w-3xl mx-auto">
                <strong>Educational Use Notice:</strong> Species information adapted from educational wildlife materials for identification and hunting reference purposes. Always verify current hunting regulations with your state wildlife agency.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}